import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  Observable,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ENV } from "./configs";
import { cache } from "./cache";
import axios from "axios";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { StorageHelper } from "./storageHelper";

const httpLink = createHttpLink({
  uri: ENV.api_url,
});
const wsLink = new GraphQLWsLink(
  createClient({
    url: ENV.socket_api_url,
    connectionParams: () => {
      const token = StorageHelper.get("token");
      return { authorization: token ? `Bearer ${token}` : "" };
    },
    shouldRetry: () => true,
  })
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);
const authLink = setContext((_, { headers }) => {
  const token = StorageHelper.get("token");
  const subinvestigator_id = StorageHelper.get("subinvestigator_id");
  const user = JSON.parse(StorageHelper.get("user"));
  const role_id = user?.role_id;

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      subinvestigatorid: subinvestigator_id ? parseInt(subinvestigator_id) : "",
      roleid: role_id ? parseInt(role_id) : "",
    },
  };
});

const refreshToken = async () => {
  try {
    const response = await axios.post(
      ENV.api_url,
      {
        query:
          "mutation RefreshToken {\n  RefreshToken {\n    access_token\n  }\n}",
        operationName: "RefreshToken",
      },
      {
        headers: {
          Authorization: `Bearer ${StorageHelper.get("token")}`,
        },
      }
    );
    const error = response.data?.errors;
    if (error) {
      window.open("/login", "_self");
      throw new Error("Session expired");
    }
    const new_token = response.data?.data?.RefreshToken?.access_token;
    if (!new_token) {
      window.open("/login", "_self");
      throw new Error("Session expired");
    }

    StorageHelper.set("token", new_token);
    return new_token;
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        if (err?.extensions?.code === "UNAUTHENTICATED") {
          if (!isRefreshing) {
            isRefreshing = true;
            return new Observable((observer) => {
              refreshToken()
                .then((token) => {
                  operation.setContext(({ headers = {} }) => ({
                    headers: {
                      ...headers,
                      authorization: `Bearer ${token}`,
                    },
                  }));
                  processQueue(null, token);
                  isRefreshing = false;
                  forward(operation).subscribe({
                    next: (result) => observer.next(result),
                    error: (error) => observer.error(error),
                    complete: () => observer.complete(),
                  });
                })
                .catch((error) => {
                  processQueue(error, null);
                  isRefreshing = false;
                  observer.error(error);
                });
            });
          }

          // Refresh already in progress — wait for the new token, then retry
          return new Observable((observer) => {
            failedQueue.push({
              resolve: (token) => {
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${token}`,
                  },
                }));
                forward(operation).subscribe({
                  next: (result) => observer.next(result),
                  error: (error) => observer.error(error),
                  complete: () => observer.complete(),
                });
              },
              reject: (error) => {
                observer.error(error);
              },
            });
          });
        }
      }
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  }
);

export const ProcessGrapqlError = ({
  graphQLErrors,
  networkError,
  ...rest
}) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(message);
    });
  if (rest) {
    console.log(rest);
  }
};

/** First GraphQL error message from an Apollo mutation error, for user-facing alerts. */
export const readMutationErrorMessage = (error) =>
  error?.graphQLErrors?.[0]?.message ||
  error?.networkError?.result?.errors?.[0]?.message ||
  error?.message ||
  "Something went wrong! Please try again";

export const apollo_client = new ApolloClient({
  link: errorLink.concat(authLink.concat(splitLink)),
  cache: cache,
});
